import { Inject, Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { WINDOW } from '@lsq-frontend/angular-shared/settings';
import { AbstractAnalyticsService } from './abstract-analytics.service';
import { AnalyticsEvent, AnalyticsProp, AnalyticsValue } from './analytics.constants';

export interface SegmentWindow extends Window {
    analytics: any;
}

@Injectable({ providedIn: 'root' })
export class SegmentAnalyticsService implements AbstractAnalyticsService {
    constructor(
        @Inject(SETTINGS) private settings: ISettings,
        @Inject(WINDOW) private _window: SegmentWindow
    ) {
        SingletonServiceGuard.register('SegmentAnalyticsService');
    }

    public initalize(internalUser: boolean): void {
        // Should be initalized.
    }

    public setPageView(path: string, title: string): void {
        if (this._window.analytics) {
            this._window.analytics.page({
                name: title
            });
        }
    }

    public setCompanyData(
        companyName: string,
        email: string,
        userKey: string,
        firstName: string,
        lastName: string,
        isAdmin: boolean
    ): void {
        if (this._window.analytics) {
            this._window.analytics.identify(userKey, {
                [AnalyticsProp.FirstName]: firstName,
                [AnalyticsProp.LastName]: lastName,
                [AnalyticsProp.CompanyName]: companyName,
                [AnalyticsProp.Email]: email,
                [AnalyticsProp.IsAdmin]: isAdmin
            });
        }
    }

    public clearIdentifiersData(): void {
        if (this._window.analytics) {
            this._window.analytics.reset();
        }
    }

    public setUserData(userName: string, email: string, userKey: string, isAdmin: boolean): void {
        if (this._window.analytics) {
            this._window.analytics.identify(userKey, {
                [AnalyticsProp.UserName]: userName,
                [AnalyticsProp.Email]: email,
                [AnalyticsProp.IsAdmin]: isAdmin
            });
        }
    }

    public triggerSingleDateFilterEvent(reportName: string, dateOffset: number): void {
        if (this._window.analytics) {
            this._window.analytics.track(AnalyticsEvent.Filter, {
                [AnalyticsProp.DateOffset]: dateOffset,
                [AnalyticsProp.ReportName]: reportName,
                [AnalyticsProp.FilterType]: AnalyticsValue.DateFilter
            });
        }
    }

    public triggerDateRangeFilterEvent(
        reportName: string,
        startDateOffset: number,
        endDateOffset: number
    ): void {
        if (this._window.analytics) {
            this._window.analytics.track(AnalyticsEvent.Filter, {
                [AnalyticsProp.StartDateOffset]: startDateOffset,
                [AnalyticsProp.EndDateOffset]: endDateOffset,
                [AnalyticsProp.ReportName]: reportName,
                [AnalyticsProp.FilterType]: AnalyticsValue.DateFilter
            });
        }
    }

    public triggerSimpleEvent(eventName: string): void {
        if (this._window.analytics) {
            this._window.analytics.track(eventName);
        }
    }

    public triggerEventWithPayload(eventName: string, data: { [key: string]: any }): void {
        if (this._window.analytics) {
            this._window.analytics.track(eventName, data);
        }
    }
}
